/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useMemo, useState } from 'react';
import { clearProductFilterEvents } from '@/actions/shelf/shelf';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { eventTypes } from '@/constants/event';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { getProductFinderAnalytics } from '@/utils/analytics/legacyUtils/getProductFinderAnalytics';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { getShelfAnalytics, getSelectedSortingOptionLabel } from '@/utils/analytics/shelfAnalytics';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { siteSections } from '@/utils/siteSections';
import { useRouter } from 'next/router';
import { getSearchVariables } from '../../utils/analytics/getSearchVariables';
import { getSearchVariablesFromProductRecords } from '../../utils/analytics/getSearchVariablesFromProductRecords';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { useIs24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import usePrevious from '@/utils/usePrevious';
import { parseUrl } from '@/utils/urlHelpers';
import { useGetCustomPriceFilterLabel } from '@/hooks/useGetCustomPriceFilterLabel';
import { useGetFilterAnalytics } from '@/hooks/useGetFilterAnalytics';
import { useGetSessionFiltersList } from '@/hooks/useGetSessionFiltersList';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import root from 'window-or-global';
import { useFeatureFlag } from '@/features/globalConfig';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { sessionStorage } from '@/utils/sessionStorage';
import { useFilteredSortingOptions } from '@/hooks/useFilteredSortingOptions';
import { useLabels } from '@/hooks/useLabels';
import { useCombinedInfiniteQuery } from '@/hooks/useCombinedInfiniteQuery';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSelector } from 'react-redux';
import { type ReduxState } from '@/types';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { useSearchProductData } from '../../api/getSearchProductData';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useProductListView } from '@/features/shelf/context/useProductListView';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { formatReviewStatistics } from '@/features/shelf/utils/formatReviewStatistics';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const labelMap = {
  featuredSortingOptionLabel: 'label.sort.featured',
};

type SearchAnalyticsParams = {
  analyticsCalled: boolean;
  analyticsEventType: string | undefined;
  viewingPartTypes: boolean;
  isLoadMoreEnabled: boolean;
  isMobile: boolean;
  setAnalyticsCalled: (called: boolean) => void;
  setViewingPartTypes: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useSearchAnalytics = ({
  analyticsCalled,
  analyticsEventType,
  viewingPartTypes,
  isLoadMoreEnabled,
  isMobile,
  setAnalyticsCalled,
  setViewingPartTypes,
}: SearchAnalyticsParams) => {
  const hasSetupAnalytics = useAnalytics();
  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const {
    pfmLocation,
    eventType: eventTypeAnalytics,
    ignoreNextPageViewCall,
  } = usePageSpecificData().pageSpecificData;
  const router = useRouter();
  const { query } = parseUrl(router.asPath);
  const prevQuery = usePrevious(query);
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const [analyticsCallSecondarySkus, setAnalyticsCallSecondarySkus] = useState(false);
  const callPageView = usePageViewCall();
  const {
    data: searchProductData,
    isSuccess: isSearchProductSuccess,
    isPending: isSearchProductPending,
    fetchStatus: searchProductFetchStatus,
  } = useSearchProductData({
    enabled: !viewingPartTypes,
  });
  const {
    data: searchPartTypesData,
    isPending: isSearchPartTypesPending,
    isSuccess: isSearchPartTypesSuccess,
    fetchStatus: searchPartTypesFetchStatus,
  } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });
  const {
    data: productListData,
    isPending: isProductListPending,
    isFetching: isProductListFetching,
    isSuccess: productListSuccess,
    productSkuIsFetching: isProductSkuFetching,
    secondaryProductSkuIsFetching,
    loadMoreClicked,
    additionalProductSkuDetailsIsFetching,
  } = useProductList();
  const { infiniteQueryIsPending } = useCombinedInfiniteQuery();
  const prevSecondaryProductSkuIsFetching = usePrevious(secondaryProductSkuIsFetching);
  const parts = productListData?.shelfParts;
  const prevParts = usePrevious(parts);
  const queryDisplayLabel =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';
  const enable24ProductView = useIs24ProductViewEnabled();
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const { selectedNullCartridge } = useSelector((state: ReduxState) => state.shelf);
  const selectedFilterValue = useSelector(({ shelf }: ReduxState) => shelf.selectedFilterValue);
  const eventType = useSelector(({ shelf }: ReduxState) => shelf.eventType);
  const { setPageSpecificData } = usePageSpecificData();
  const dispatch = useDispatch();
  const dispatchClearProductFilterEvents = () => dispatch(clearProductFilterEvents());
  const getCustomPriceFilterLabel = useGetCustomPriceFilterLabel();
  const getFilterAnalytics = useGetFilterAnalytics();
  const sessionFiltersList = useGetSessionFiltersList();
  const { data: headerData } = useHeaderData();
  const preferredVehicle = usePreferredVehicle();
  const vehicleName = preferredVehicle?.vehicleName;
  const dealDrawerShelfEnabled = useFeatureFlag('DEAL_DRAWER_SHELF_ENABLED') === 'true';
  const storeSelected = useIsStoreSelected();
  const dealBadgeEnabled = dealDrawerShelfEnabled && storeSelected;
  const [sortingOptions = []] = useFilteredSortingOptions() || [];
  const { featuredSortingOptionLabel } = useLabels(labelMap);
  const correctedTermData = searchProductData?.correctedTerm ?? searchPartTypesData?.correctedTerm;
  const visualCategoriesLengthToDisplay = isMobile ? 12 : isTablet ? 5 : 7;
  const visualCategoryAnalytic = searchProductData?.partTypeRecords
    ?.slice(0, visualCategoriesLengthToDisplay)
    .map((category) => category.partTypeName)
    .join(', ');

  const availableFilters = searchProductData?.staticNavigation
    ?.find((filter) => filter.dimensionName === 'part_type_name_s')
    ?.refinements.map((refinement) => `${refinement.label} (${refinement.count})`)
    .join(', ');

  const analyticsParts = useMemo(() => {
    if (!productListData?.shelfParts) {
      return [];
    }
    return enable24ProductView && !isLoadMoreEnabled
      ? analyticsCallSecondarySkus
        ? productListData.shelfParts.slice(12)
        : productListData.shelfParts.slice(0, 12)
      : isLoadMoreEnabled && loadMoreClicked
      ? productListData.shelfParts.slice(-24)
      : productListData.shelfParts;
  }, [
    productListData?.shelfParts,
    enable24ProductView,
    isLoadMoreEnabled,
    analyticsCallSecondarySkus,
    loadMoreClicked,
  ]);

  const { listViewState } = useProductListView();
  const isList = isBrandShelfPage(router.asPath)
    ? listViewState.isBrandListView
    : listViewState.isListView;

  useEffect(() => {
    if (
      analyticsCalled &&
      (query.sort !== prevQuery?.sort ||
        query.recsPerPage !== prevQuery?.recsPerPage ||
        query.pageNumber !== prevQuery?.pageNumber)
    ) {
      setAnalyticsCalled(false);
      setAnalyticsCallSecondarySkus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, prevQuery, analyticsCalled]);

  useEffect(() => {
    if (prevSecondaryProductSkuIsFetching && !secondaryProductSkuIsFetching && analyticsCalled) {
      setAnalyticsCalled(false);
      setAnalyticsCallSecondarySkus(true);
    }
  }, [
    secondaryProductSkuIsFetching,
    prevSecondaryProductSkuIsFetching,
    setAnalyticsCalled,
    analyticsCalled,
  ]);

  const enableVdpDsUniqueDisplay = useMonetateDecisionFlag('enableVdpDsUniqueDisplay');

  const analyticsData = getShelfAnalytics({
    isList,
    parts: analyticsParts,
    selectedNullCartridge,
    getCustomPriceFilterLabel,
    query,
    getFilterAnalytics,
    sessionFiltersList,
    headerData,
    productFilterData: productListData?.productFilterData,
    makeOrModelName: vehicleName,
    defaultEngine: preferredVehicle?.engine,
    staticNavigation: searchProductData?.staticNavigation ?? searchPartTypesData?.staticNavigation,
    dealBadgeEnabled,
    enableVdpDsUniqueDisplay,
    acesEnabled: acesEnabled && acesNonVehicleEndpointsEnabled,
  });

  const { data: reviewStatistics } = useReviewStatistics({
    skuNumbers: analyticsData.productIdList,
    enabled: true,
  });

  useEffect(() => {
    if (
      hasSetupAnalytics &&
      ((isSearchProductSuccess && productListSuccess) || isSearchPartTypesSuccess) &&
      !isProductListFetching &&
      !isProductSkuFetching &&
      !secondaryProductSkuIsFetching &&
      !isProductListPending &&
      searchPartTypesFetchStatus !== 'fetching' &&
      searchProductFetchStatus !== 'fetching' &&
      ((!viewingPartTypes && !searchProductData?.redirectUrl) ||
        (viewingPartTypes && !isSearchPartTypesPending) ||
        (searchPartTypesData &&
          !searchPartTypesData?.redirectLocation &&
          isSearchPartTypesSuccess &&
          !isSearchPartTypesPending &&
          !viewingPartTypes)) &&
      (viewingPartTypes ? !isSearchPartTypesPending : !isSearchProductPending) &&
      queryDisplayLabel &&
      !ignoreNextPageViewCall &&
      !analyticsCalled &&
      (loadMoreClicked
        ? !additionalProductSkuDetailsIsFetching &&
          !infiniteQueryIsPending &&
          parts?.length !== prevParts?.length
        : true)
    ) {
      const searchVariables = viewingPartTypes
        ? getSearchVariables([searchPartTypesData!])
        : getSearchVariablesFromProductRecords(
            searchProductData?.firstRecNum ?? searchPartTypesData?.firstRecNum ?? 0,
            searchProductData?.records ?? []
          );
      const productStrikeThroughPricing: string[] = [];
      analyticsParts?.forEach((part) => {
        const strikethroughProduct = getProductStrikethroughAnalyticValue(
          part.skuPricingAndAvailability,
          part.skuPricingAndAvailability?.discountedPrice,
          strikeThroughPricingEnabled
        );
        productStrikeThroughPricing.push(strikethroughProduct[0]);
      });

      const { productAverageRating, productReviewCount } = formatReviewStatistics(
        reviewStatistics,
        analyticsData.productIdList
      );

      const shelfAnalyticsData = viewingPartTypes
        ? {
            searchResultCount: `${searchPartTypesData?.total}`,
          }
        : {
            ...analyticsData,
            pageDepth: `${searchProductData?.firstRecNum ?? searchPartTypesData?.firstRecNum} to ${
              searchProductData?.lastRecNum ?? searchPartTypesData?.lastRecNum
            } of ${searchProductData?.total ?? searchPartTypesData?.total}`,
            resultsPerPage: searchProductData?.recordsPerPage
              ? searchProductData.recordsPerPage
              : '',
            productAverageRating,
            productReviewCount,
            firstRecNum: searchProductData?.firstRecNum,
            lastRecNum: searchProductData?.lastRecNum,
            searchResultCount: `${searchProductData?.total}`,
          };

      const productFindingMethod =
        typeof root.utag_data?.productFindingMethod === 'string'
          ? root.utag_data?.productFindingMethod
          : 'Internal Search';

      callPageView({
        pageName: getPageName(
          viewingPartTypes ? pageTypes.catalogSearchResults : pageTypes.catalogNewSearchResults,
          ''
        ),
        siteSection: siteSections.siteSectionCatalogNew,
        pageType: viewingPartTypes
          ? pageTypes.catalogSearchResults
          : pageTypes.catalogNewSearchResults,
        productFindingMethod,
        pfmLocation,
        productStrikeThroughPricing: productStrikeThroughPricing,
        prodView: analyticsParts?.length ? eventTypes.prodView : '',
        skuView: (analyticsParts ?? []).map(() => '1'),
        seoPageType: getSeoPageType(
          viewingPartTypes ? pageTypes.catalogSearchResults : pageTypes.catalogNewSearchResults,
          router.asPath
        ),
        productFitmentBanner:
          eventTypeAnalytics === eventTypes.productFitmentBanner ? eventTypeAnalytics : '',
        productToastNotification:
          eventTypeAnalytics === eventTypes.productToastNotification ? eventTypeAnalytics : '',
        ...getProductFinderAnalytics(productListData?.productFinderData),
        ...shelfAnalyticsData,
        ...searchVariables,
        selectedFilterValue,
        productPartList: analyticsParts?.map((part) => part.name),
        searchMethod: sessionStorage.getItem('searchMethod') || '',
        searchType: searchProductData?.isUnitOfMeasureMatch
          ? 'UnitOfMeasure'
          : searchProductData?.interchangeSearchFlag
          ? 'CrossReference'
          : undefined,
        selectedsortValue: getSelectedSortingOptionLabel(
          String(query.sort) || null,
          sortingOptions,
          featuredSortingOptionLabel
        ),
        autoCorrectedKeyword: correctedTermData ? correctedTermData : '',
        eventType:
          eventType ||
          (!searchProductData?.records?.length && !searchPartTypesData?.records?.length
            ? 'nullSearch'
            : loadMoreClicked
            ? 'loadMore'
            : !sessionStorage.getItem('returnToSearchResults')
            ? analyticsEventType
            : undefined),
        pageLazyLoad: enable24ProductView && analyticsCallSecondarySkus,
        lwTestExperience: searchProductData?.lwTestExperience,
        xFusionQueryId: searchProductData?.xFusionQueryId,
        visualPartTypeDisplayed: visualCategoryAnalytic,
        partTypeFiltersAvailable: availableFilters,
      });
      setPageSpecificData({ ...searchVariables, productFindingMethod });
      dispatchClearProductFilterEvents();
      sessionStorage.removeItem('searchMethod');
      sessionStorage.removeItem('returnToSearchResults');
      sessionStorage.setItem('performedSearch', 'false');
      setAnalyticsCalled(true);
    }
    if (query.searchText !== prevQuery?.searchText && query.partTypesView !== 'true') {
      setViewingPartTypes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSetupAnalytics,
    productListSuccess,
    searchPartTypesFetchStatus,
    searchProductFetchStatus,
    isProductListFetching,
    isProductSkuFetching,
    secondaryProductSkuIsFetching,
    router.asPath,
    preferredVehicle?.catalogVehicleId,
    analyticsCalled,
    isProductListPending,
    isSearchPartTypesPending,
    isSearchProductPending,
    analyticsParts,
    additionalProductSkuDetailsIsFetching,
    infiniteQueryIsPending,
    loadMoreClicked,
    productListData,
    productListData?.shelfParts,
  ]);
};
